import type { DirectionCategoryId } from '../../utils/annotationCategories';
import type { TranscriptMark } from '../../utils/transcriptMarks';
import type { ShortcutGroup } from '../AnnotationStudio/ShortcutHelpDialog';

/**
 * Marking the transcript: what a studio passes to the transcript panel so
 * the creator can pick words and mark them Remove, Short or Note, and what
 * the panel asks the studio to save. The panel works out the times, quote
 * and merges (utils/transcriptMarks); the studio only saves and deletes.
 */

/** A Remove or Short to save. */
export interface TranscriptMarkRequest {
  kind: DirectionCategoryId;
  /** Timeline seconds. */
  start: number;
  end: number;
  /** “The quoted words”, with the comments of any Removes it absorbs. */
  text: string;
  /** Removes this one absorbs: delete them once it is saved. */
  replaces: TranscriptMark[];
}

export interface TranscriptMarkingProps {
  /** The creator's notes and marks, timed on the timeline (no camera cuts). */
  marks: TranscriptMark[];
  /** The note selected in the studio (in the notes list, on the timeline or here). */
  selectedMarkId?: string | null;
  /** Show the marks, but no way to add or delete them: a submitted set, the editor's view. */
  readOnly?: boolean;
  /** Why marking can't be used yet (the notes are still loading), shown in place of the actions. */
  disabledReason?: string | null;
  /** A mark was clicked: select it and go there. */
  onSelectMark?: (mark: TranscriptMark) => void;
  /** The selected mark was closed in the transcript. */
  onClearSelection?: () => void;
  /** Save a Remove or Short. Resolves true once saved (the selection then clears). */
  onMark?: (request: TranscriptMarkRequest) => Promise<boolean> | boolean;
  onDeleteMark?: (mark: TranscriptMark) => void;
  /** Open the studio's edit dialog for the mark (to add a comment, or change its times). */
  onEditMark?: (mark: TranscriptMark) => void;
}

/** The transcript's keys, for the studios' shortcut lists (they work once a word is clicked, or the words are Tabbed to). */
export const TRANSCRIPT_SHORTCUTS: ShortcutGroup = {
  title: 'Transcript',
  items: [
    { keys: ['← →', '↑ ↓'], label: 'Move between words and lines' },
    { keys: ['Shift', '← →'], label: 'Select words' },
    { keys: ['R'], label: 'Remove the selected words from the edit' },
    { keys: ['S'], label: 'Make the selected words a short' },
    { keys: ['N'], label: 'Add a note on the selected words' },
    { keys: ['Enter'], label: 'Select the word, or the mark it’s in' },
    { keys: ['Delete'], label: 'Delete the selected mark (you can undo it)' },
    { keys: ['Esc'], label: 'Clear the selection' },
  ],
};
